<script setup lang="ts">
import { useToastStore, type ToastType } from '@/stores/toastStore'
import { storeToRefs } from 'pinia'

const toastStore = useToastStore()
const { toasts } = storeToRefs(toastStore)

const iconMap: Record<ToastType, string> = {
  success: 'M5 13l4 4L19 7',
  error: 'M6 18L18 6M6 6l12 12',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
}

const colorMap: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: {
    bg: 'linear-gradient(135deg, #FFECDF 0%, #FFF5EB 100%)',
    border: '#EE8544',
    text: '#EE8544',
  },
  error: {
    bg: 'linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)',
    border: '#EF4444',
    text: '#EF4444',
  },
  warning: {
    bg: 'linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%)',
    border: '#F59E0B',
    text: '#F59E0B',
  },
  info: {
    bg: 'linear-gradient(135deg, #DBEAFE 0%, #EFF6FF 100%)',
    border: '#3B82F6',
    text: '#3B82F6',
  },
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast" tag="div">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-notification"
          :style="{
            background: colorMap[toast.type].bg,
            borderColor: colorMap[toast.type].border,
          }"
        >
          <div class="toast-content">
            <svg
              class="toast-icon"
              :style="{ color: colorMap[toast.type].text }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                :d="iconMap[toast.type]"
              />
            </svg>
            <span
              class="toast-message"
              :style="{ color: colorMap[toast.type].text }"
            >
              {{ toast.message }}
            </span>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.toast-container {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.toast-notification {
  border: 2px solid;
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 280px;
  max-width: 90vw;
  backdrop-filter: blur(10px);
  pointer-events: auto;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
}

.toast-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.toast-message {
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.2px;
}

// Toast Animation
.toast-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-move {
  transition: transform 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.toast-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.toast-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

// Mobile responsive
@media (max-width: 640px) {
  .toast-container {
    bottom: 16px;
    left: 16px;
    right: 16px;
    transform: none;
  }

  .toast-notification {
    min-width: auto;
    width: 100%;
  }

  .toast-content {
    padding: 14px 20px;
  }

  .toast-enter-from,
  .toast-enter-to,
  .toast-leave-from,
  .toast-leave-to {
    transform: translateY(20px);
  }
  
  .toast-enter-to,
  .toast-leave-from {
    transform: translateY(0);
  }
}
</style>

